import { useState, useRef, useCallback } from "react";
import { toast } from "sonner";
import {
  Upload,
  Brain,
  CheckCircle2,
  FileText,
  Download,
  Share2,
  RotateCcw,
  ChevronRight,
  AlertTriangle,
  Sparkles,
  FlaskConical,
  User,
  Calendar,
  Phone,
  Stethoscope,
  ClipboardList,
} from "lucide-react";

import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import { erpApi } from "../services/erpApi";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const LAB_PROVIDERS = [
  "Dr Lal PathLabs",
  "SRL Diagnostics",
  "Thyrocare",
  "Metropolis",
  "Redcliffe",
  "PMCH Lab",
  "Kshipra Lab",
  "Other",
];

const STEPS = [
  { id: 1, label: "Upload Report", icon: Upload },
  { id: 2, label: "AI Extraction", icon: Brain },
  { id: 3, label: "Review Data", icon: ClipboardList },
  { id: 4, label: "Generate PDF", icon: FileText },
];

const STATUS_CLASSES = {
  High: "bg-red-50 text-red-700 border border-red-200 font-semibold",
  Low: "bg-amber-50 text-amber-700 border border-amber-200 font-semibold",
  Normal: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Positive: "bg-red-50 text-red-700 border border-red-200 font-semibold",
  Negative: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  Critical: "bg-red-100 text-red-800 border border-red-300 font-bold",
  Abnormal: "bg-red-50 text-red-700 border border-red-200 font-semibold",
};

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function StepIndicator({ currentStep }) {
  return (
    <div className="flex items-center justify-center gap-0 mb-8">
      {STEPS.map((step, idx) => {
        const Icon = step.icon;
        const done = currentStep > step.id;
        const active = currentStep === step.id;
        return (
          <div key={step.id} className="flex items-center">
            <div className="flex flex-col items-center gap-1">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  done
                    ? "bg-emerald-600 text-white shadow-md"
                    : active
                    ? "bg-white border-2 border-emerald-600 text-emerald-600 shadow-lg scale-110"
                    : "bg-gray-100 text-gray-400 border border-gray-200"
                }`}
              >
                {done ? (
                  <CheckCircle2 className="w-5 h-5" />
                ) : (
                  <Icon className="w-4 h-4" />
                )}
              </div>
              <span
                className={`text-xs font-medium hidden sm:block ${
                  active ? "text-emerald-700" : done ? "text-emerald-600" : "text-gray-400"
                }`}
              >
                {step.label}
              </span>
            </div>
            {idx < STEPS.length - 1 && (
              <div
                className={`w-12 sm:w-20 h-0.5 mx-1 mt-[-18px] sm:mt-[-18px] transition-all duration-500 ${
                  currentStep > step.id ? "bg-emerald-500" : "bg-gray-200"
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

function StatusBadge({ status }) {
  if (!status) return <span className="text-gray-400 text-xs">—</span>;
  const cls = STATUS_CLASSES[status] || "bg-gray-100 text-gray-600 border border-gray-200";
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${cls}`}>
      {status}
    </span>
  );
}

function InfoCard({ icon: Icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3 p-3 bg-white rounded-lg border border-gray-100 shadow-sm">
      <div className="w-8 h-8 rounded-full bg-emerald-50 flex items-center justify-center flex-shrink-0">
        <Icon className="w-4 h-4 text-emerald-600" />
      </div>
      <div>
        <p className="text-xs text-gray-500 font-medium">{label}</p>
        <p className="text-sm text-gray-800 font-semibold">{value}</p>
      </div>
    </div>
  );
}

function ClinicalSection({ label, value }) {
  if (!value) return null;
  return (
    <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
      <p className="text-xs font-bold text-emerald-700 uppercase tracking-wide mb-1">{label}</p>
      <p className="text-sm text-gray-800 whitespace-pre-wrap">{value}</p>
    </div>
  );
}

function ExtractionBadge({ source, model }) {
  if (!source) return null;
  const isGemini = source === "gemini" || source === "gemini_pdf";
  const label = source === "gemini_pdf"
    ? `Gemini PDF Vision (${model || "gemini-2.0-flash"})`
    : isGemini
    ? `Gemini AI (${model || "gemini-2.0-flash"})`
    : "Heuristic Fallback";
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${
        isGemini
          ? "bg-violet-100 text-violet-700 border border-violet-200"
          : "bg-amber-100 text-amber-700 border border-amber-200"
      }`}
    >
      {isGemini ? (
        <Sparkles className="w-3 h-3" />
      ) : (
        <AlertTriangle className="w-3 h-3" />
      )}
      {label}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Extraction animation steps
// ---------------------------------------------------------------------------
const EXTRACTION_STAGES = [
  { label: "Reading PDF text layer…", duration: 900 },
  { label: "Sending to Gemini AI…", duration: 1800 },
  { label: "Extracting medical parameters…", duration: 1200 },
  { label: "Validating structured data…", duration: 700 },
  { label: "Finalizing report…", duration: 400 },
];

function ExtractionAnimation({ stage }) {
  return (
    <div className="flex flex-col items-center gap-6 py-10">
      {/* Pulse circle */}
      <div className="relative w-24 h-24">
        <div className="absolute inset-0 rounded-full bg-emerald-200 animate-ping opacity-60" />
        <div className="relative w-24 h-24 rounded-full bg-emerald-600 flex items-center justify-center shadow-xl">
          <Brain className="w-10 h-10 text-white animate-pulse" />
        </div>
      </div>
      {/* Stage list */}
      <div className="space-y-2 w-full max-w-xs">
        {EXTRACTION_STAGES.map((s, idx) => (
          <div
            key={s.label}
            className={`flex items-center gap-2 text-sm transition-all duration-300 ${
              idx < stage
                ? "text-emerald-600 font-medium"
                : idx === stage
                ? "text-gray-800 font-semibold"
                : "text-gray-300"
            }`}
          >
            <span
              className={`w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center text-xs ${
                idx < stage
                  ? "bg-emerald-500 text-white"
                  : idx === stage
                  ? "border-2 border-emerald-500 bg-white"
                  : "border border-gray-200 bg-gray-50"
              }`}
            >
              {idx < stage ? "✓" : ""}
            </span>
            {s.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------
export default function ReportStandardizer() {
  const [step, setStep] = useState(1);

  // Step 1 state
  const [selectedFile, setSelectedFile] = useState(null);
  const [provider, setProvider] = useState("");
  const [overridePatientName, setOverridePatientName] = useState("");
  const [overridePatientMobile, setOverridePatientMobile] = useState("");
  const [overrideTestName, setOverrideTestName] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // Step 2 state
  const [extractionStage, setExtractionStage] = useState(0);
  const [extractionSource, setExtractionSource] = useState("");
  const [extractionModel, setExtractionModel] = useState("");
  const [validationWarnings, setValidationWarnings] = useState([]);

  // Step 3 state
  const [structuredReport, setStructuredReport] = useState(null);

  // Step 4 state
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);

  // ---------------------------------------------------------------------------
  // Step 1 — Upload
  // ---------------------------------------------------------------------------
  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type === "application/pdf") {
      setSelectedFile(file);
    } else {
      toast.error("Please drop a PDF file");
    }
  }, []);

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
    e.target.value = "";
  };

  // ---------------------------------------------------------------------------
  // Step 2 — Extraction
  // ---------------------------------------------------------------------------
  const runExtractionAnimation = useCallback(() => {
    return new Promise((resolve) => {
      let s = 0;
      const tick = () => {
        if (s >= EXTRACTION_STAGES.length) {
          resolve();
          return;
        }
        setExtractionStage(s);
        setTimeout(() => {
          s++;
          tick();
        }, EXTRACTION_STAGES[Math.min(s, EXTRACTION_STAGES.length - 1)]?.duration ?? 500);
      };
      tick();
    });
  }, []);

  const runExtraction = useCallback(async () => {
    if (!selectedFile) return;
    setStep(2);
    setExtractionStage(0);

    const formData = new FormData();
    formData.append("file", selectedFile);
    if (provider) formData.append("provider", provider);
    if (overridePatientName) formData.append("patient_name", overridePatientName);
    if (overridePatientMobile) formData.append("patient_mobile", overridePatientMobile);
    if (overrideTestName) formData.append("test_name", overrideTestName);

    // Run animation and API call in parallel
    const [, response] = await Promise.allSettled([
      runExtractionAnimation(),
      erpApi.standardizePreview(formData),
    ]);

    if (response.status === "rejected") {
      const detail = response.reason?.response?.data?.detail || response.reason?.message || "Extraction failed";
      toast.error(detail);
      setStep(1);
      return;
    }

    const data = response.value.data;
    setStructuredReport(data.structured_report);
    setExtractionSource(data.extraction_source || "");
    setExtractionModel(data.extraction_model || "");
    setValidationWarnings(data.validation_warnings || []);

    // Small delay so the "Finalizing…" stage shows
    await new Promise((r) => setTimeout(r, 600));
    setStep(3);
  }, [selectedFile, provider, overridePatientName, overridePatientMobile, overrideTestName, runExtractionAnimation]);

  // ---------------------------------------------------------------------------
  // Step 4 — Generate PDF
  // ---------------------------------------------------------------------------
  const generatePdf = useCallback(async () => {
    if (!structuredReport) return;
    setIsGenerating(true);
    try {
      const response = await erpApi.standardizeGeneratePdf({ structured_report: structuredReport });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      setPdfBlobUrl(url);
      setStep(4);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "PDF generation failed");
    } finally {
      setIsGenerating(false);
    }
  }, [structuredReport]);

  const downloadPdf = () => {
    if (!pdfBlobUrl) return;
    const patientName = structuredReport?.patient?.name || "report";
    const safeName = patientName.replace(/\s+/g, "_");
    const a = document.createElement("a");
    a.href = pdfBlobUrl;
    a.download = `standardized_${safeName}.pdf`;
    a.click();
  };

  const shareWhatsApp = () => {
    const mobile = structuredReport?.patient?.mobile_number || "";
    const phone = mobile.replace(/\D/g, "");
    const wa = phone.length === 10 ? `91${phone}` : phone;
    const name = structuredReport?.patient?.name || "Patient";
    const test = structuredReport?.report?.test_name || "lab test";
    const text = `Hello ${name}, your standardized report for ${test} is ready. Please download your PDF report.`;
    window.open(`https://wa.me/${wa}?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  };

  const resetAll = () => {
    setStep(1);
    setSelectedFile(null);
    setProvider("");
    setOverridePatientName("");
    setOverridePatientMobile("");
    setOverrideTestName("");
    setStructuredReport(null);
    setExtractionSource("");
    setExtractionModel("");
    setValidationWarnings([]);
    if (pdfBlobUrl) window.URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
    setIsGenerating(false);
  };

  // ---------------------------------------------------------------------------
  // Derived data
  // ---------------------------------------------------------------------------
  const patient = structuredReport?.patient || {};
  const reportMeta = structuredReport?.report || {};
  const sections = structuredReport?.sections || [];
  const clinicalSections = []; // Keep as empty array to avoid breaking later usage

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-teal-50/20">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Page header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center shadow-md">
              <FlaskConical className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Report Standardizer</h1>
              <p className="text-sm text-gray-500">
                Upload any lab PDF → AI extracts data → Generates ATDC hospital report
              </p>
            </div>
          </div>
        </div>

        {/* Step indicator */}
        <StepIndicator currentStep={step} />

        {/* Step panels */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">

          {/* ────────────────── STEP 1: UPLOAD ────────────────── */}
          {step === 1 && (
            <div className="p-6 md:p-8 space-y-6">
              <div>
                <h2 className="text-lg font-bold text-gray-800 mb-1">Upload Lab Report PDF</h2>
                <p className="text-sm text-gray-500">
                  Supports Dr Lal PathLabs, SRL, Thyrocare, Metropolis, Redcliffe, PMCH, and any digital PDF.
                </p>
              </div>

              {/* Drop zone */}
              <div
                id="pdf-drop-zone"
                onDragEnter={() => setIsDragging(true)}
                onDragLeave={() => setIsDragging(false)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={handleFileDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`relative cursor-pointer rounded-xl border-2 border-dashed p-10 flex flex-col items-center gap-3 transition-all duration-200 ${
                  isDragging
                    ? "border-emerald-500 bg-emerald-50 scale-[1.01]"
                    : selectedFile
                    ? "border-emerald-400 bg-emerald-50/60"
                    : "border-gray-200 hover:border-emerald-300 hover:bg-gray-50"
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={handleFileInput}
                />
                {selectedFile ? (
                  <>
                    <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                      <FileText className="w-7 h-7 text-emerald-600" />
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-emerald-700 text-sm">{selectedFile.name}</p>
                      <p className="text-xs text-gray-400 mt-0.5">
                        {(selectedFile.size / 1024).toFixed(1)} KB · Click to change
                      </p>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center">
                      <Upload className="w-7 h-7 text-gray-400" />
                    </div>
                    <div className="text-center">
                      <p className="font-semibold text-gray-700">Drop PDF here or click to browse</p>
                      <p className="text-xs text-gray-400 mt-0.5">Only PDF files are supported</p>
                    </div>
                  </>
                )}
              </div>

              {/* Provider & context */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label className="text-sm">Source Lab / Provider</Label>
                  <Select value={provider} onValueChange={setProvider}>
                    <SelectTrigger id="provider-select">
                      <SelectValue placeholder="Select lab (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {LAB_PROVIDERS.map((lab) => (
                        <SelectItem key={lab} value={lab}>{lab}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm">Patient Name Override</Label>
                  <Input
                    id="patient-name-override"
                    placeholder="Optional — from DB if blank"
                    value={overridePatientName}
                    onChange={(e) => setOverridePatientName(e.target.value)}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm">Patient Mobile Override</Label>
                  <Input
                    id="patient-mobile-override"
                    placeholder="Optional"
                    value={overridePatientMobile}
                    onChange={(e) => setOverridePatientMobile(e.target.value)}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-sm">Test Name Override</Label>
                  <Input
                    id="test-name-override"
                    placeholder="Optional"
                    value={overrideTestName}
                    onChange={(e) => setOverrideTestName(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button
                  id="start-extraction-btn"
                  className="btn-primary gap-2"
                  disabled={!selectedFile}
                  onClick={runExtraction}
                >
                  Start AI Extraction
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* ────────────────── STEP 2: EXTRACTION ────────────────── */}
          {step === 2 && (
            <div className="p-8">
              <h2 className="text-lg font-bold text-gray-800 mb-2 text-center">AI Extraction in Progress</h2>
              <p className="text-sm text-gray-400 text-center mb-4">
                Gemini is reading and structuring the medical data…
              </p>
              <ExtractionAnimation stage={extractionStage} />
            </div>
          )}

          {/* ────────────────── STEP 3: REVIEW ────────────────── */}
          {step === 3 && structuredReport && (
            <div className="p-6 md:p-8 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-gray-800">Review Extracted Data</h2>
                  <p className="text-sm text-gray-500">Verify before generating the hospital report</p>
                </div>
                <ExtractionBadge source={extractionSource} model={extractionModel} />
              </div>

              {/* Validation warnings */}
              {validationWarnings.length > 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 space-y-1">
                  <p className="text-xs font-bold text-amber-700 flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Extraction Warnings
                  </p>
                  {validationWarnings.map((w, i) => (
                    <p key={i} className="text-xs text-amber-600 pl-4">{w}</p>
                  ))}
                </div>
              )}

              {/* Patient info */}
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Patient Information</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  <InfoCard icon={User} label="Patient Name" value={patient.name} />
                  <InfoCard icon={User} label="Age / Gender" value={[patient.age, patient.gender].filter(Boolean).join(" / ")} />
                  <InfoCard icon={Phone} label="Mobile" value={patient.mobile_number} />
                  <InfoCard icon={Calendar} label="Sample Date" value={patient.sample_date} />
                  <InfoCard icon={Calendar} label="Report Date" value={patient.report_date} />
                  <InfoCard icon={FlaskConical} label="Source Lab" value={reportMeta.lab_name} />
                  <InfoCard icon={Stethoscope} label="Test Name" value={reportMeta.test_name} />
                  {reportMeta.referring_doctor && (
                    <InfoCard icon={Stethoscope} label="Referring Doctor" value={reportMeta.referring_doctor} />
                  )}
                </div>
              </div>

              {/* Dynamic sections */}
              {sections.length > 0 && (
                <div className="space-y-6 mt-6">
                  {sections.map((sec, secIdx) => (
                    <div key={secIdx} className="space-y-2">
                      {sec.title && (
                        <h3 className="text-sm font-bold text-emerald-800 uppercase tracking-wider">{sec.title}</h3>
                      )}
                      
                      {sec.type === 'table' && (sec.table_headers?.length > 0 || sec.table_rows?.length > 0) && (
                        <div className="rounded-xl border border-gray-200 overflow-hidden">
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              {sec.table_headers?.length > 0 && (
                                <thead>
                                  <tr className="bg-emerald-50 border-b border-emerald-100">
                                    {sec.table_headers.map((h, i) => (
                                      <th key={i} className="text-left px-4 py-2.5 font-semibold text-emerald-800 text-xs">{h}</th>
                                    ))}
                                  </tr>
                                </thead>
                              )}
                              <tbody>
                                {sec.table_rows?.map((row, rIdx) => (
                                  <tr key={rIdx} className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${rIdx % 2 === 0 ? "bg-white" : "bg-gray-50/30"}`}>
                                    {row.map((cell, cIdx) => (
                                      <td key={cIdx} className="px-4 py-2.5 text-gray-700">{cell}</td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                      
                      {sec.type === 'list' && sec.list_items?.length > 0 && (
                        <ul className="list-decimal pl-5 space-y-1 text-sm text-gray-700">
                          {sec.list_items.map((item, i) => (
                            <li key={i}>{item}</li>
                          ))}
                        </ul>
                      )}
                      
                      {sec.type === 'text' && sec.text_content && (
                        <div className="text-sm text-gray-700 whitespace-pre-wrap rounded-xl border border-emerald-100 bg-emerald-50/30 p-4">
                          {sec.text_content}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Clinical sections */}
              {clinicalSections.length > 0 && (
                <div className="space-y-3">
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Clinical Sections</p>
                  {clinicalSections.map(([label, value]) => (
                    <ClinicalSection key={label} label={label} value={value} />
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="flex flex-col sm:flex-row justify-between gap-3 pt-2 border-t border-gray-100">
                <Button variant="outline" className="gap-2" onClick={() => setStep(1)}>
                  <RotateCcw className="w-4 h-4" />
                  Back to Upload
                </Button>
                <Button
                  id="generate-pdf-btn"
                  className="btn-primary gap-2"
                  onClick={generatePdf}
                  disabled={isGenerating}
                >
                  {isGenerating ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Building PDF…
                    </>
                  ) : (
                    <>
                      <FileText className="w-4 h-4" />
                      Generate Standardized PDF
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* ────────────────── STEP 4: PDF ────────────────── */}
          {step === 4 && pdfBlobUrl && (
            <div className="p-6 md:p-8 space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-gray-800">Standardized Report Ready</h2>
                  <p className="text-sm text-gray-500">
                    ATDC hospital-branded report generated successfully
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  <span className="text-sm font-semibold text-emerald-700">Complete</span>
                </div>
              </div>

              {/* PDF preview */}
              <div className="rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                <iframe
                  src={pdfBlobUrl}
                  title="Standardized Lab Report"
                  className="w-full"
                  style={{ height: "520px" }}
                />
              </div>

              {/* Action buttons */}
              <div className="flex flex-wrap gap-3">
                <Button
                  id="download-pdf-btn"
                  className="btn-primary gap-2"
                  onClick={downloadPdf}
                >
                  <Download className="w-4 h-4" />
                  Download PDF
                </Button>

                {structuredReport?.patient?.mobile_number && (
                  <Button variant="outline" className="gap-2 text-emerald-700 border-emerald-300 hover:bg-emerald-50" onClick={shareWhatsApp}>
                    <Share2 className="w-4 h-4" />
                    Share via WhatsApp
                  </Button>
                )}

                <Button variant="outline" className="gap-2" onClick={() => window.open(pdfBlobUrl, "_blank", "noopener,noreferrer")}>
                  <FileText className="w-4 h-4" />
                  Open in New Tab
                </Button>

                <Button variant="outline" className="gap-2 ml-auto" onClick={resetAll}>
                  <RotateCcw className="w-4 h-4" />
                  Process Another
                </Button>
              </div>

              {/* Patient summary card */}
              <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
                <p className="text-xs font-bold text-emerald-700 mb-2">Report Summary</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                  <div><span className="text-gray-500">Patient: </span><span className="font-medium">{patient.name || "—"}</span></div>
                  <div><span className="text-gray-500">Lab: </span><span className="font-medium">{reportMeta.lab_name || "—"}</span></div>
                  <div><span className="text-gray-500">Sections: </span><span className="font-medium">{sections.length}</span></div>
                  <div><span className="text-gray-500">Source: </span><ExtractionBadge source={extractionSource} model={extractionModel} /></div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
